import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import StatsOverview from '../ResultsTracker/StatsOverview';
import PerformanceChart from '../ResultsTracker/PerformanceChart';
import BookmakerBreakdown from '../ResultsTracker/BookmakerBreakdown';
import MonthlyCalendar from '../ResultsTracker/MonthlyCalendar';
import SportBreakdown from '../ResultsTracker/SportBreakdown';
import RoundBreakdown from '../ResultsTracker/RoundBreakdown';
import { TOOL_LABELS } from './CommunityBetTracker';
import ToolBreakdown from './ToolBreakdown';
import TipperBreakdown from './TipperBreakdown';
import PublicTipLog from './PublicTipLog';
import SubscriptionRequired from '../common/SubscriptionRequired';
import Toast from '../Toast/Toast';
import communityTipsTrackerService from '../../utils/services/communityTipsTrackerService';
import { useAuth } from '../../context/AuthContext';

/**
 * Public Community Tips — the read-only analytical view of community tips,
 * available to PRO/ELITE subscribers (the backend enforces entitlement on the
 * GET endpoint; free-tier users get the SubscriptionRequired gate).
 *
 * This intentionally mirrors the admin CommunityTipsTracker (Analytics +
 * Tip Log views) but strips every management affordance: no add/edit/delete/
 * reorder, and results render as static chips. Admin management stays in
 * Admin Tools → Community Tip Result Tracker; results graded there flow
 * through here automatically via the shared GET endpoint.
 */

// Community tip stakes are stored as units already, so we display unit values
// directly (unitSize === 1). There is no per-user unit-size configuration.
const UNIT_SIZE = 1;

// Normalises a tip date to a "YYYY-MM" bucket. Reads the leading YYYY-MM off
// the string directly (avoids timezone roll-over from Date parsing), falling
// back to Date parsing for any non-ISO formats.
const getMonthKey = (dateStr) => {
    if (!dateStr) return null;
    const m = String(dateStr).match(/(\d{4})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}`;
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime())
        ? null
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Module-level UI-state cache kept alive for the SPA session, so leaving the
// page (e.g. deep-linking into the Prop Tool) and coming back restores the
// active view and filters instead of resetting to defaults.
const pageStateCache = {};

function PublicCommunityTips() {
    const { logout } = useAuth();

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [toast, setToast] = useState(null);

    const [tips, setTips] = useState([]);

    // Active (pending) tips enriched with live odds — fetched separately via
    // `GET /community/tips?type=active` whenever the Tip Log view is opened.
    // `null` means "never loaded" (distinguishes from a legitimately empty list).
    const [activeTips, setActiveTips] = useState(null);
    const [isActiveTipsLoading, setIsActiveTipsLoading] = useState(false);
    const [activeTipsError, setActiveTipsError] = useState(null);

    const [activeView, setActiveView] = useState(pageStateCache.activeView ?? 'overview'); // 'overview' or 'tracker'
    const [isViewTransitioning, setIsViewTransitioning] = useState(false);
    const viewChangeTimeoutRef = useRef(null);

    const displayMode = 'units'; // community tips are always denominated in units
    const [sportFilter, setSportFilter] = useState(pageStateCache.sportFilter ?? 'all');
    const [tipperFilter, setTipperFilter] = useState(pageStateCache.tipperFilter ?? 'all');
    const [toolFilter, setToolFilter] = useState(pageStateCache.toolFilter ?? 'all');
    const [timeFilter, setTimeFilter] = useState(pageStateCache.timeFilter ?? 'all'); // 'all' or 'YYYY-MM'
    // Mobile-only: whether the collapsible filter row is expanded.
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // Stash the current view/filters so a return visit within this session
    // (e.g. back from the Prop Tool) picks up where the user left off.
    useEffect(() => {
        pageStateCache.activeView = activeView;
        pageStateCache.sportFilter = sportFilter;
        pageStateCache.tipperFilter = tipperFilter;
        pageStateCache.toolFilter = toolFilter;
        pageStateCache.timeFilter = timeFilter;
    }, [activeView, sportFilter, tipperFilter, toolFilter, timeFilter]);

    const showToast = useCallback((message, type = 'error', duration = 5000) => {
        setToast({ message, type, duration });
    }, []);

    const handleAuthError = useCallback((errorCode) => {
        let message = 'Please login to continue.';
        if (errorCode === 'TOKEN_EXPIRED') {
            message = 'Your session has expired. Please login again.';
        }
        showToast(message, 'warning');
        setTimeout(() => logout(), 1500);
    }, [logout, showToast]);

    // Load tips on mount
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setLoadError(null);

            const result = await communityTipsTrackerService.getAllData();

            if (result.success) {
                setTips(result.data.tips || []);
                setTimeout(() => setIsPageLoaded(true), 50);
            } else if (result.isAuthError) {
                handleAuthError(result.errorCode);
            } else if (result.isSubscriptionError) {
                setLoadError(result);
            } else {
                setLoadError(result);
                showToast(result.message, 'error');
            }

            setIsLoading(false);
        };

        loadData();
    }, [handleAuthError, showToast]);

    useEffect(() => {
        return () => {
            if (viewChangeTimeoutRef.current) clearTimeout(viewChangeTimeoutRef.current);
        };
    }, []);

    const fetchActiveTips = useCallback(async () => {
        setIsActiveTipsLoading(true);
        setActiveTipsError(null);

        const result = await communityTipsTrackerService.getActiveTips();

        if (result.success) {
            setActiveTips(result.data.tips || []);
        } else if (result.isAuthError) {
            handleAuthError(result.errorCode);
        } else {
            setActiveTipsError(result);
            showToast(result.message, 'error');
        }

        setIsActiveTipsLoading(false);
    }, [handleAuthError, showToast]);

    // (Re)load the active tips each time the Tip Log view is opened so the
    // live-odds enrichment is fresh; previously loaded data stays on screen
    // while the refresh happens in the background.
    useEffect(() => {
        if (activeView === 'tracker') {
            fetchActiveTips();
        }
    }, [activeView, fetchActiveTips]);

    const handleViewChange = (newView) => {
        if (newView === activeView) return;
        if (viewChangeTimeoutRef.current) clearTimeout(viewChangeTimeoutRef.current);
        setIsViewTransitioning(true);
        viewChangeTimeoutRef.current = setTimeout(() => {
            setActiveView(newView);
            setTimeout(() => setIsViewTransitioning(false), 50);
        }, 200);
    };

    const availableSports = useMemo(() => {
        const hardcoded = ['afl', 'nba'];
        const hardcodedSet = new Set(hardcoded);
        const extraSports = [...new Set(tips.map(t => t.sport).filter(s => s && !hardcodedSet.has(s)))].sort();
        return [...hardcoded, ...extraSports];
    }, [tips]);

    // Unique tippers for the tipper filter
    const tippers = useMemo(() => {
        const map = new Map();
        tips.forEach(t => {
            if (t.user_id == null) return;
            if (!map.has(t.user_id)) {
                map.set(t.user_id, {
                    user_id: t.user_id,
                    label: t.tipper_discord_name || t.tipper_email || `User #${t.user_id}`
                });
            }
        });
        return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
    }, [tips]);

    // Tools present in the data (drives the Tool filter dropdown)
    const availableTools = useMemo(() => {
        return [...new Set(tips.map(t => t.tipped_tool).filter(Boolean))]
            .sort((a, b) => (TOOL_LABELS[a] || a).localeCompare(TOOL_LABELS[b] || b));
    }, [tips]);

    // Available month/year buckets present in the data, newest first. Value is
    // a "YYYY-MM" key; label is a friendly "July 2026".
    const monthOptions = useMemo(() => {
        const keys = new Set();
        tips.forEach(t => {
            const k = getMonthKey(t.date);
            if (k) keys.add(k);
        });
        return [...keys].sort().reverse().map(k => {
            const [y, m] = k.split('-');
            const label = new Date(Number(y), Number(m) - 1, 1)
                .toLocaleString('en-US', { month: 'long', year: 'numeric' });
            return { value: k, label };
        });
    }, [tips]);

    // Friendly label for the active month filter, used as a section-title
    // suffix. Null when viewing all time.
    const timeRangeLabel = useMemo(() => {
        if (timeFilter === 'all') return null;
        return monthOptions.find(o => o.value === timeFilter)?.label || null;
    }, [timeFilter, monthOptions]);

    // Number of non-default filters, shown as a badge on the mobile toggle.
    const activeFilterCount = [tipperFilter, sportFilter, toolFilter, timeFilter]
        .filter(v => v !== 'all').length;

    // Filter by sport + tipper + tool + time range
    const filteredTips = useMemo(() => {
        return tips.filter(t => {
            if (sportFilter !== 'all' && t.sport !== sportFilter) return false;
            if (tipperFilter !== 'all' && String(t.user_id) !== String(tipperFilter)) return false;
            if (toolFilter !== 'all' && t.tipped_tool !== toolFilter) return false;
            if (timeFilter !== 'all' && getMonthKey(t.date) !== timeFilter) return false;
            return true;
        });
    }, [tips, sportFilter, tipperFilter, toolFilter, timeFilter]);

    // High-level all-time record per tipper (wins-losses, P/L and settled
    // stake for ROI), computed from the full tips listing and shown alongside
    // each active tip. Keyed by the tipper's display label (lowercased) since
    // that's the only identifier shared between the plain and `type=active`
    // listings.
    //
    // Deliberately all-time: unlike the Tipper Leaderboard (which reflects the
    // page's filters) this is a career record. ROI uses the same settled-only
    // basis as the leaderboard so the two agree over the same set of tips.
    const tipperRecords = useMemo(() => {
        const map = {};
        tips.forEach(t => {
            const label = t.tipper_discord_name || t.tipper_email || (t.user_id != null ? `User #${t.user_id}` : 'Unknown');
            const key = label.toLowerCase();
            if (!map[key]) map[key] = { wins: 0, losses: 0, pl: 0, staked: 0 };
            if (t.result === 'win') map[key].wins += 1;
            else if (t.result === 'loss') map[key].losses += 1;
            // Settled = anything graded (win / loss / cash out). Stake and P/L
            // are accumulated together so a cash-out can't contribute profit
            // without its matching stake.
            if (t.result && t.result !== 'pending') {
                map[key].staked += Number(t.stake) || 0;
                map[key].pl += t.pl || 0;
            }
        });
        return map;
    }, [tips]);

    // Same page-level filters applied to the active-tips list feeding the
    // Tip Log view.
    const filteredActiveTips = useMemo(() => {
        return (activeTips || []).filter(t => {
            if (sportFilter !== 'all' && t.sport !== sportFilter) return false;
            if (tipperFilter !== 'all' && String(t.user_id) !== String(tipperFilter)) return false;
            if (toolFilter !== 'all' && t.tipped_tool !== toolFilter) return false;
            if (timeFilter !== 'all' && getMonthKey(t.date) !== timeFilter) return false;
            return true;
        });
    }, [activeTips, sportFilter, tipperFilter, toolFilter, timeFilter]);

    // Stats (mirrors Results Tracker; no bonus bets in community tips)
    const stats = useMemo(() => {
        const totalBets = filteredTips.length;
        const wins = filteredTips.filter(b => b.result === 'win').length;
        const losses = filteredTips.filter(b => b.result === 'loss').length;
        const pending = filteredTips.filter(b => b.result === 'pending').length;
        const cashedOut = filteredTips.filter(b => b.result === 'cash out').length;
        const winRate = (wins + losses) > 0 ? ((wins / (wins + losses)) * 100).toFixed(2) : 0;

        const totalStaked = filteredTips.reduce((sum, b) => sum + b.stake, 0);
        const totalPL = filteredTips.reduce((sum, b) => sum + b.pl, 0);
        const settledBets = filteredTips.filter(b => b.result !== 'pending');
        const totalReturned = settledBets.reduce((sum, b) => sum + b.stake + b.pl, 0);
        const roi = totalStaked > 0 ? ((totalPL / totalStaked) * 100).toFixed(2) : 0;

        const avgOdds = totalBets > 0 ? (filteredTips.reduce((sum, b) => sum + b.odds, 0) / totalBets).toFixed(2) : 0;
        const winningBets = filteredTips.filter(b => b.result === 'win');
        const avgWinOdds = winningBets.length > 0
            ? (winningBets.reduce((sum, b) => sum + b.odds, 0) / winningBets.length).toFixed(2)
            : 0;

        let currentStreak = 0;
        let currentStreakType = null;
        let longestWinStreak = 0;
        let longestLossStreak = 0;
        let tempWinStreak = 0;
        let tempLossStreak = 0;

        const sortedBets = [...filteredTips].sort((a, b) => new Date(a.date) - new Date(b.date));
        sortedBets.forEach(bet => {
            if (bet.result === 'win') {
                tempWinStreak++;
                tempLossStreak = 0;
                longestWinStreak = Math.max(longestWinStreak, tempWinStreak);
            } else if (bet.result === 'loss') {
                tempLossStreak++;
                tempWinStreak = 0;
                longestLossStreak = Math.max(longestLossStreak, tempLossStreak);
            }
        });

        for (let i = sortedBets.length - 1; i >= 0; i--) {
            const bet = sortedBets[i];
            if (bet.result === 'pending' || bet.result === 'cash out') continue;
            if (currentStreakType === null) {
                currentStreakType = bet.result;
                currentStreak = 1;
            } else if (bet.result === currentStreakType) {
                currentStreak++;
            } else {
                break;
            }
        }

        return {
            totalBets, wins, losses, pending, cashedOut,
            winRate: parseFloat(winRate),
            totalStaked, totalReturned, totalPL,
            roi: parseFloat(roi),
            avgOdds: parseFloat(avgOdds),
            avgWinOdds: parseFloat(avgWinOdds),
            currentStreak, currentStreakType, longestWinStreak, longestLossStreak
        };
    }, [filteredTips]);

    const cumulativePLData = useMemo(() => {
        const sortedBets = [...filteredTips].sort((a, b) => new Date(a.date) - new Date(b.date));
        let cumulative = 0;
        return sortedBets.map(bet => {
            cumulative += bet.pl;
            return { date: bet.date, pl: bet.pl, cumulative, game: bet.game, bet: bet.bet };
        });
    }, [filteredTips]);

    // Loading state
    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center bg-[#020817] min-h-screen">
                <div className="flex flex-col items-center gap-3">
                    <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: '200ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: '400ms' }} />
                    </div>
                    <span className="text-xs font-mono text-slate-500">Loading community tips</span>
                </div>
            </div>
        );
    }

    // Subscription gate — the GET requires a PRO/ELITE subscription
    if (loadError?.isSubscriptionError) {
        return (
            <div className="flex-1 flex items-center justify-center bg-[#020817] min-h-screen">
                <SubscriptionRequired feature="community tips analytics" variant="page" flush />
            </div>
        );
    }

    // Error state
    if (loadError && tips.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center bg-[#020817] min-h-screen p-4">
                <div className="max-w-md w-full bg-red-500/5 border border-red-500/15 rounded p-6 text-center">
                    <div className="w-10 h-10 bg-red-500/10 border border-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-sm font-semibold font-mono text-white mb-2 uppercase tracking-widest">Failed to Load Tips</h3>
                    <p className="text-xs font-mono text-slate-500 leading-relaxed mb-5">{loadError.message}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded text-xs font-mono font-medium hover:bg-emerald-500/15 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const selectArrowStyle = {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
        backgroundSize: '14px'
    };

    const toolFilterSelect = (extraClass = '') => (
        <select
            value={toolFilter}
            onChange={(e) => setToolFilter(e.target.value)}
            className={`px-3 py-1.5 bg-slate-800 border border-slate-700/50 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none pr-8 ${extraClass}`}
            style={selectArrowStyle}
        >
            <option value="all">All Tools</option>
            {availableTools.map(tool => (
                <option key={tool} value={tool}>{TOOL_LABELS[tool] || tool}</option>
            ))}
        </select>
    );

    const timeFilterSelect = (extraClass = '') => (
        <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className={`px-3 py-1.5 bg-slate-800 border border-slate-700/50 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none pr-8 ${extraClass}`}
            style={selectArrowStyle}
        >
            <option value="all">All Time</option>
            {monthOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );

    const viewToggleButtons = (compact = false) => (
        <>
            <button
                onClick={() => handleViewChange('overview')}
                className={`${compact ? 'flex-1 px-3 py-1.5 justify-center' : 'px-3 py-1'} rounded text-[11px] font-mono font-medium transition-all flex items-center gap-1.5 ${activeView === 'overview' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-white'}`}
            >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Tipper Results
            </button>
            <button
                onClick={() => handleViewChange('tracker')}
                className={`${compact ? 'flex-1 px-3 py-1.5 justify-center' : 'px-3 py-1'} rounded text-[11px] font-mono font-medium transition-all flex items-center gap-1.5 ${activeView === 'tracker' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-white'}`}
            >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Community Tips Feed
            </button>
        </>
    );

    return (
        <div className="min-h-screen flex flex-col bg-[#020817] p-2 md:p-4 overflow-hidden">
            {toast && (
                <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={() => setToast(null)} />
            )}

            {/* Command Bar */}
            <div className={`mb-3 bg-[#0f172a] border border-slate-700/50 rounded transition-all duration-500 ${isPageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                {/* Desktop Header */}
                <div className="hidden md:flex items-center justify-between px-4 py-3 gap-3">
                    <div className="flex items-center gap-3">
                        <h2 className="text-sm font-semibold text-white tracking-tight">Community Tips</h2>
                        <div className="w-px h-5 bg-slate-700/50" />
                        <div className="flex bg-slate-800/50 border border-slate-700/50 rounded p-0.5">
                            {viewToggleButtons(false)}
                        </div>
                        <div className="w-px h-5 bg-slate-700/50" />
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-800/50 rounded">
                            <span className="text-[11px] font-mono font-semibold text-white">{activeView === 'tracker' ? filteredActiveTips.length : filteredTips.length}</span>
                            <span className="text-[11px] font-mono text-slate-600">{activeView === 'tracker' ? 'active tips' : 'tips'}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <select
                            value={tipperFilter}
                            onChange={(e) => setTipperFilter(e.target.value)}
                            className="px-3 py-1.5 bg-slate-800 border border-slate-700/50 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none pr-8 max-w-[180px] truncate"
                            style={selectArrowStyle}
                        >
                            <option value="all">All Tippers</option>
                            {tippers.map(t => (
                                <option key={t.user_id} value={t.user_id}>{t.label}</option>
                            ))}
                        </select>

                        <select
                            value={sportFilter}
                            onChange={(e) => setSportFilter(e.target.value)}
                            className="px-3 py-1.5 bg-slate-800 border border-slate-700/50 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none pr-8"
                            style={selectArrowStyle}
                        >
                            <option value="all">All Sports</option>
                            {availableSports.map(sport => (
                                <option key={sport} value={sport}>{sport.toUpperCase()}</option>
                            ))}
                        </select>

                        {toolFilterSelect('max-w-[180px] truncate')}

                        {timeFilterSelect()}
                    </div>
                </div>

                {/* Mobile Header */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-semibold text-white tracking-tight">Community Tips</h2>
                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-800/50 rounded">
                                <span className="text-[11px] font-mono font-semibold text-white">{activeView === 'tracker' ? filteredActiveTips.length : filteredTips.length}</span>
                                <span className="text-[11px] font-mono text-slate-600">{activeView === 'tracker' ? 'active tips' : 'tips'}</span>
                            </div>
                        </div>

                        {/* Expandable filter toggle (mobile only) */}
                        <button
                            type="button"
                            onClick={() => setMobileFiltersOpen((v) => !v)}
                            aria-expanded={mobileFiltersOpen}
                            aria-label="Toggle filters"
                            className={`flex items-center justify-center px-2.5 py-1.5 rounded-lg border transition-colors ${
                                mobileFiltersOpen
                                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                    : 'border-slate-700/60 bg-slate-900/50 text-slate-300'
                            }`}
                        >
                            <span className="relative inline-flex">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M6 12h12M10 17h4" />
                                </svg>
                                {activeFilterCount > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-1 rounded-full bg-emerald-500 text-emerald-950 text-[8px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </span>
                        </button>
                    </div>

                    <div className="flex bg-slate-800/30 mx-3 mb-2 border border-slate-700/50 rounded p-0.5">
                        {viewToggleButtons(true)}
                    </div>

                    <div className={`${mobileFiltersOpen ? 'grid' : 'hidden'} px-3 pb-3 grid-cols-2 gap-2 border-t border-slate-700/30 pt-2.5`}>
                        <select
                            value={tipperFilter}
                            onChange={(e) => setTipperFilter(e.target.value)}
                            className="w-full px-3 py-1.5 bg-slate-800 border border-slate-700/50 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500 appearance-none pr-8 truncate"
                            style={selectArrowStyle}
                        >
                            <option value="all">All Tippers</option>
                            {tippers.map(t => (
                                <option key={t.user_id} value={t.user_id}>{t.label}</option>
                            ))}
                        </select>
                        <select
                            value={sportFilter}
                            onChange={(e) => setSportFilter(e.target.value)}
                            className="w-full px-3 py-1.5 bg-slate-800 border border-slate-700/50 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500 appearance-none pr-8"
                            style={selectArrowStyle}
                        >
                            <option value="all">All Sports</option>
                            {availableSports.map(sport => (
                                <option key={sport} value={sport}>{sport.toUpperCase()}</option>
                            ))}
                        </select>
                        {toolFilterSelect('w-full truncate')}
                        {timeFilterSelect('w-full truncate')}
                    </div>
                </div>
            </div>

            {/* Content — flex-1 so the Tip Log card can stretch to fill the viewport */}
            <div className={`flex-1 flex flex-col transition-all duration-300 ease-out ${isViewTransitioning ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'}`}>
                {activeView === 'overview' ? (
                    <div className="space-y-3">
                        <div className={`transition-all duration-500 ${isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '100ms' }}>
                            <StatsOverview stats={stats} displayMode={displayMode} unitSize={UNIT_SIZE} bets={filteredTips} advancedUnitSettings={false} unitDecimals={2} />
                        </div>

                        <div className={`transition-all duration-500 ${isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '150ms' }}>
                            <PerformanceChart data={cumulativePLData} bets={filteredTips} displayMode={displayMode} unitSize={UNIT_SIZE} titleSuffix={timeRangeLabel} />
                        </div>

                        {(toolFilter === 'all' || tipperFilter === 'all') && (
                            <div className={`grid grid-cols-1 ${toolFilter === 'all' && tipperFilter === 'all' ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-3 transition-all duration-500 ${isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '175ms' }}>
                                {toolFilter === 'all' && <ToolBreakdown bets={filteredTips} unitSize={UNIT_SIZE} titleSuffix={timeRangeLabel} />}
                                {tipperFilter === 'all' && <TipperBreakdown bets={filteredTips} unitSize={UNIT_SIZE} titleSuffix={timeRangeLabel} />}
                            </div>
                        )}

                        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-3 transition-all duration-500 ${isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '200ms' }}>
                            <BookmakerBreakdown bets={filteredTips} displayMode={displayMode} unitSize={UNIT_SIZE} advancedUnitSettings={false} titleSuffix={timeRangeLabel} />
                            <MonthlyCalendar bets={filteredTips} displayMode={displayMode} unitSize={UNIT_SIZE} advancedUnitSettings={false} unitDecimals={2} />
                        </div>

                        {sportFilter === 'all' && (
                            <div className={`transition-all duration-500 ${isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '250ms' }}>
                                <SportBreakdown bets={filteredTips} displayMode={displayMode} unitSize={UNIT_SIZE} advancedUnitSettings={false} />
                            </div>
                        )}

                        {sportFilter === 'afl' && (
                            <div className={`transition-all duration-500 ${isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '300ms' }}>
                                <RoundBreakdown bets={filteredTips} displayMode={displayMode} unitSize={UNIT_SIZE} advancedUnitSettings={false} />
                            </div>
                        )}
                    </div>
                ) : (
                    <PublicTipLog
                        tips={filteredActiveTips}
                        tipperRecords={tipperRecords}
                        displayMode={displayMode}
                        unitSize={UNIT_SIZE}
                        isLoading={isActiveTipsLoading && activeTips === null}
                        error={activeTips === null ? activeTipsError : null}
                        onRetry={fetchActiveTips}
                        onToast={showToast}
                    />
                )}
            </div>
        </div>
    );
}

export default PublicCommunityTips;
